// frontend/src/components/layout/MobileNavBar.tsx
import React from 'react';
import { Package, Settings2, BarChart3, Zap } from 'lucide-react';
import { hapticFeedback } from '../../utils/nativeBridge';

export type MobileTab = 'inputs' | 'securing' | 'results';

interface MobileNavBarProps {
    activeTab: MobileTab;
    setActiveTab: (tab: MobileTab) => void;
    onCalculate: () => void;
    isCalculating: boolean;
    hasResults: boolean;
    t: (key: string) => string;
}

export const MobileNavBar: React.FC<MobileNavBarProps> = ({
    activeTab,
    setActiveTab,
    onCalculate,
    isCalculating,
    hasResults,
    t
}) => {
    const handleTabChange = (tab: MobileTab) => {
        hapticFeedback('light');
        setActiveTab(tab);
    };

    const handleCalculateClick = () => {
        hapticFeedback('medium');
        onCalculate();
    };

    return (
        <div className="fixed bottom-[4.5rem] left-0 right-0 z-50 lg:hidden px-3 pointer-events-none">
            <div className="max-w-md mx-auto bg-white/90 dark:bg-[#0D111C]/90 backdrop-blur-2xl border border-slate-200/80 dark:border-white/10 rounded-2xl p-1.5 shadow-[0_12px_35px_rgba(0,0,0,0.18)] pointer-events-auto flex items-center justify-between gap-1">
                
                {/* Tab 1: Cargo (Inputs) */}
                <button
                    onClick={() => handleTabChange('inputs')}
                    className={`flex-1 flex flex-col items-center justify-center py-2 px-1 rounded-xl transition-all duration-200 active:scale-95 ${
                        activeTab === 'inputs'
                            ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 font-bold shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800'
                    }`}
                >
                    <Package className="w-5 h-5 mb-0.5" />
                    <span className="text-[10px] tracking-tight whitespace-nowrap">Cargo</span>
                </button>

                {/* Tab 2: Securing */}
                <button
                    onClick={() => handleTabChange('securing')}
                    className={`flex-1 flex flex-col items-center justify-center py-2 px-1 rounded-xl transition-all duration-200 active:scale-95 ${
                        activeTab === 'securing'
                            ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 font-bold shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800'
                    }`}
                >
                    <Settings2 className="w-5 h-5 mb-0.5" />
                    <span className="text-[10px] tracking-tight whitespace-nowrap">Securing</span>
                </button>

                {/* Center Floating Action Calculate Button (FAB) */}
                <button
                    onClick={handleCalculateClick}
                    disabled={isCalculating}
                    className="flex-shrink-0 flex items-center justify-center w-12 h-12 bg-gradient-to-tr from-orange-600 to-amber-500 text-white rounded-xl shadow-lg shadow-orange-600/35 active:scale-90 transition-all border border-orange-400/30 mx-1"
                    title="Calculate"
                >
                    <Zap className={`w-6 h-6 ${isCalculating ? 'animate-spin' : ''}`} />
                </button>

                {/* Tab 3: Results & 3D */}
                <button
                    onClick={() => handleTabChange('results')}
                    className={`flex-1 flex flex-col items-center justify-center py-2 px-1 rounded-xl transition-all duration-200 active:scale-95 relative ${
                        activeTab === 'results'
                            ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 font-bold shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800'
                    }`}
                >
                    <div className="relative">
                        <BarChart3 className="w-5 h-5 mb-0.5" />
                        {hasResults && (
                            <span className="absolute -top-1 -right-1.5 w-2 h-2 bg-emerald-500 rounded-full animate-ping" />
                        )}
                    </div>
                    <span className="text-[10px] tracking-tight whitespace-nowrap">Results & 3D</span>
                </button>

            </div>
        </div>
    );
};

export default MobileNavBar;
